import {
  Box,
  Button,
  Checkbox,
  Flashbar,
  FormField,
  Grid,
  Input,
  Modal,
  Popover,
  Select,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import {
  CapMaterials,
  Materials,
  ProcessNames,
  ProcessStepMap,
} from '@parallel-fluidics/shared/constants';
import { decodePPartId } from '@parallel-fluidics/shared/models/projects/parts';
import MaterialSelector from 'components/HMI/components/materialSelector';
import ProjectSelector from 'components/HMI/components/projectSelector';
import StepSelector from 'components/HMI/components/stepSelector';
import ToolSelector from 'components/HMI/components/toolSelector';
import {
  Timestamp,
} from 'firebase/firestore';
import { Form, FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as Yup from 'yup';

import createProcessLog from '../../processLog/api/createProcessLog';
import getNextProcessLogNumber from '../../processLog/api/getNextProcessLogNumber';
import PistonSelector from './PistonSelector';
import TravelerInput from './travelerInput';

function StartRunModalShell({
  isOpen,
  close,
  process,
  press,
  plcConfig = null,
  onStarted,
  onContextChange = null,
  configSection = null,
  skipAction = null,
  configError = null,
  isStartDisabled = false,
  startDisabledMessage = null,
  warningItems = [],
  bondingDefaults = {},
}) {
  const { user } = useUserData();

  const initialValues = useMemo(() => ({
    process,
    press,
    project: null,
    selectedStep: null,
    selectedStepValue: null,
    hypothesis: '',
    number: 0,
    production: false,
    travelers: [],
    skipTravelers: false,
    // forming line specific
    material: null,
    otherMaterial: '',
    piston: '',
    tool: {},
    toolMap: {},
    capMaterial: null,
    // bonding line specific
    numberOfChips: 1,
    pressOrientation: bondingDefaults.pressOrientation || '',
  }), [bondingDefaults.pressOrientation, press, process]);

  const validationSchema = Yup.object().shape({
    project: Yup.object().shape({
      value: Yup.string().required('Project is required'),
    }).nullable().required('Project is required'),
    selectedStep: Yup.object().nullable().when(['project.steps', 'skipTravelers'], {
      is: (steps, skipTravelers) => Array.isArray(steps) && steps.length > 0 && !skipTravelers,
      then: Yup.object().required('Step is required'),
    }),
    hypothesis: Yup.string().when('production', {
      is: false,
      then: Yup.string().required('Hypothesis is required'),
    }),
    number: Yup.number().typeError('Chip number is required').min(1, 'Chip number must be greater than zero'),
    travelers: Yup.array(Yup.string()).when('skipTravelers', {
      is: false,
      then: Yup.array(Yup.string()).min(1, 'Add at least one traveler or toggle the skip travelers button'),
    }),
    // Operators sometimes scan travelers while "Skip travelers" is still checked, which would
    // silently drop those travelers from the process log on submit. A `test` (instead of `when`)
    // avoids a cyclic dependency with the travelers rule above.
    skipTravelers: Yup.boolean().test(
      'no-travelers-when-skipping',
      'Travelers have been scanned. Uncheck "Skip travelers" or remove the scanned travelers.',
      (skipTravelers, context) => !(skipTravelers && context.parent.travelers?.length > 0),
    ),
    // forming line specific
    material: Yup.object().nullable().when('process', {
      is: ProcessNames.FORMING,
      then: (schema) => schema.required('Material is required'),
    }),
    otherMaterial: Yup.string().when('material.value', {
      is: 'other',
      then: Yup.string().required('Material is required'),
    }),
    piston: Yup.string().when('process', {
      is: ProcessNames.FORMING,
      then: Yup.string().required('Piston is required'),
    }),
    tool: Yup.object().when('process', {
      is: ProcessNames.FORMING,
      then: Yup.object().shape({
        value: Yup.string().required('Tool name is required'),
      }),
    }),
    toolMap: Yup.mixed().when('process', {
      is: ProcessNames.FORMING,
      then: Yup.mixed()
        .test(
          'toolMap-length',
          'Travelers must match or less than the number of cavities',
          (toolMap, context) => {
            if (!toolMap || typeof toolMap !== 'object') return false;
            // checking that every traveler in context.parent.travelers is found in toolMap
            const { travelers } = context.parent;
            const allTravelersMapped = travelers.every((traveler) => !!toolMap[traveler]);
            if (!allTravelersMapped) return false;
            return Object.keys(toolMap).length === travelers.length;
          },
        )
        .test(
          'toolMap-values',
          'Cavity values must be unique',
          (toolMap) => {
            if (!toolMap || typeof toolMap !== 'object') return false;
            const toolMapValues = Object.values(toolMap);
            const uniqueValues = new Set(toolMapValues);
            return uniqueValues.size === toolMapValues.length;
          },
        ),
    }),
    // bonding line specific
    capMaterial: Yup.object().nullable().when('process', {
      is: ProcessNames.BONDING,
      then: (schema) => schema.required('Cap Material is required'),
    }),
    numberOfChips: Yup.number().when('process', {
      is: ProcessNames.BONDING,
      then: Yup.number().moreThan(0, 'Number of chips must be greater than zero').required('Number of chips is required'),
    }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setSubmitting, setFieldValue }) => {
      // One clock reading is both the doc's `start` and the anchor handed to the
      // PLC, so telemetry (start + elapsed) lines up with this value exactly.
      const startMs = Date.now();
      const logData = {
        process,
        press,
        user: user?.id,
        start: Timestamp.fromMillis(startMs),
        hypothesis: values.hypothesis,
        project: values.project?.projectId || '',
        pPart: values.project?.pPartId || '',
        production: values.production,
        number: values.number,
        travelers: values.skipTravelers ? [] : values.travelers || [],
        plcConfig,
      };

      if (values.project?.orderId) {
        logData.order = values.project.orderId;
      }
      if (values.project?.lineItemId) {
        logData.lineItem = values.project.lineItemId;
      }
      if (values.selectedStep?.value >= 0) {
        logData.currentStepIndex = values.selectedStep.value;
      }
      // Store the description observed by the operator at the start of the run
      if (values.selectedStep?.descriptionText) {
        logData.currentStepNote = values.selectedStep.descriptionText;
      }
      if (values.selectedStep?.pPartId) {
        logData.stepPPartId = values.selectedStep.pPartId;
      }

      if (values.process === ProcessNames.FORMING) {
        let material = values.material?.value || '';
        if (values.material?.value === 'other') {
          material = values.otherMaterial;
        }
        logData.material = material;
        logData.piston = values.piston;
        logData.tool = values.tool.value;
        logData.toolMap = values.toolMap;
      }

      if (values.process === ProcessNames.BONDING) {
        logData.capMaterial = values.capMaterial?.value || '';
        logData.numberOfChips = values.numberOfChips;
        logData.pressOrientation = values.pressOrientation;
      }

      const docRef = await createProcessLog(logData);

      setFieldValue('hypothesis', '');
      setFieldValue('number', 0);
      setFieldValue('travelers', []);
      // Stay in the submitting state until the PLC writes finish, so a second
      // click can't create another process log while the first is starting.
      await onStarted({ processLogId: docRef.id, startMs });
      setSubmitting(false);
      close();
    },
  });

  const {
    values,
    errors,
    handleSubmit,
    setFieldValue,
    isSubmitting,
  } = formik;

  const [stepOptions, setStepOptions] = useState([]);

  const updateChipNumber = useCallback(async (project, pPart, stepPPart) => {
    if (project) {
      const nextNumber = await getNextProcessLogNumber({
        project,
        process,
        pPart: stepPPart ? null : pPart,
        stepPPartId: stepPPart,
      });

      setFieldValue('number', nextNumber);
    }
  }, [process, setFieldValue]);

  const updateSelectedSteps = useCallback((lineItemSteps) => {
    if (!lineItemSteps?.length) return null;
    const options = [];
    let enabledCount = 0;
    let defaultSelected = null;
    lineItemSteps.forEach((step, index) => {
      const enabled = step.type === ProcessStepMap[process];
      const option = {
        value: index,
        pPartId: step.pPartId,
        pPartDocId: step.pPartDocId,
        disabled: !enabled,
        fallbackName: step.name || step.type,
        fallbackNote: step.note || '',
      };
      if (enabled && !defaultSelected) {
        defaultSelected = option;
      }
      if (enabled) enabledCount += 1;
      options.push(option);
    });
    setStepOptions(options);
    if (defaultSelected && enabledCount === 1) {
      setFieldValue('selectedStepValue', defaultSelected.value);
    }
    return defaultSelected;
  }, [process, setFieldValue]);

  const handleStepChanged = useCallback((step) => {
    setFieldValue('selectedStep', step);
  }, [setFieldValue]);

  useEffect(() => {
    if (values.selectedStep?.pPartId) {
      const { material } = decodePPartId(values.selectedStep.pPartId);
      if (material) {
        const materialsArr = process === ProcessNames.FORMING
          ? Object.values(Materials)
          : CapMaterials;
        const isInList = materialsArr.includes(material);
        setFieldValue(
          process === ProcessNames.FORMING ? 'material' : 'capMaterial',
          isInList ? { label: material, value: material } : null,
        );
      }
    }
  }, [process, setFieldValue, values.selectedStep]);

  useEffect(() => {
    if (isOpen && values.project) {
      const selectedStep = updateSelectedSteps(values.project?.steps);
      updateChipNumber(
        values.project?.projectId,
        values.project?.pPartId,
        selectedStep?.pPartId,
      );
    }
    return () => {
      setFieldValue('number', 1);
      setFieldValue('selectedStepValue', null);
    };
  }, [isOpen, setFieldValue, updateChipNumber, updateSelectedSteps, values.project]);

  useEffect(() => {
    if (onContextChange) {
      onContextChange({
        production: values.production,
        project: values.project,
        projectId: values.project?.projectId || null,
        pPartId: values.project?.pPartId || null,
        selectedStep: values.selectedStep,
        stepPPartId: values.selectedStep?.pPartId || null,
        materialValue: values.material?.value?.toLowerCase() || null,
        capMaterialValue: values.capMaterial?.value?.toLowerCase() || null,
      });
    }
  }, [
    onContextChange,
    values.capMaterial,
    values.material,
    values.production,
    values.project,
    values.selectedStep,
  ]);

  const onToolChange = useCallback((tool) => {
    setFieldValue('tool', tool);
    setFieldValue('toolMap', {});
  }, [setFieldValue]);

  const onPistonChange = useCallback((value) => setFieldValue('piston', value), [setFieldValue]);

  return (
    <FormikProvider value={formik}>
      <Form>
        <Modal
          size="max"
          visible={isOpen}
          onDismiss={close}
          header="Start"
          footer={(
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={close}>Cancel</Button>
                {skipAction ? (
                  <Popover
                    position="top"
                    size="large"
                    triggerType="custom"
                    header="Are you sure you want to skip? No data will be saved."
                    content={(
                      <SpaceBetween size="xs">
                        <img
                          alt="Are you sure?"
                          src="https://media.giphy.com/media/qUIm5wu6LAAog/giphy.gif"
                          width="100%"
                        />
                        <Button
                          variant="primary"
                          onClick={async () => {
                            await skipAction();
                            close();
                          }}
                        >
                          Confirm
                        </Button>
                      </SpaceBetween>
                    )}
                  >
                    <Button variant="link">Skip</Button>
                  </Popover>
                ) : null}
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={isStartDisabled}
                >
                  Start
                </Button>
              </SpaceBetween>
            </Box>
          )}
        >
          <SpaceBetween size="m">
            {configError ? (
              <Flashbar
                items={[{
                  header: 'There was an error figuring out what config info to save.',
                  type: 'error',
                  content: `The original error was: ${configError}`,
                  dismissible: false,
                  id: 'config_error',
                }]}
              />
            ) : null}
            {startDisabledMessage ? (
              <Flashbar
                items={[{
                  type: 'error',
                  content: startDisabledMessage,
                  dismissible: false,
                  id: 'start_disabled',
                }]}
              />
            ) : null}
            {warningItems.length ? <Flashbar items={warningItems} /> : null}
            <Grid gridDefinition={[{ colspan: 8 }, { colspan: 2 }, { colspan: 2 }]}>
              <ProjectSelector
                setProject={(newProject) => {
                  setFieldValue('project', newProject);
                  // Reset the step with the line item: until the new line
                  // item's step resolves, the old step's linked parts would
                  // otherwise drive auto-selection for the new run.
                  setFieldValue('selectedStep', null);
                  setFieldValue('selectedStepValue', null);
                  setFieldValue('material', null);
                  setFieldValue('capMaterial', null);
                  setFieldValue('piston', '');
                  setFieldValue('tool', {});
                  setFieldValue('toolMap', {});
                  setFieldValue('hypothesis', '');
                }}
                value={values.project}
                error={errors.project}
              />
              <FormField label="Chip number" description={<br />} errorText={errors.number}>
                <Input
                  onChange={({ detail }) => setFieldValue('number', parseInt(detail.value, 10))}
                  value={String(values.number)}
                  inputMode="numeric"
                  type="number"
                />
              </FormField>
              <FormField label="Production run" description={<br />}>
                <Checkbox
                  onChange={({ detail }) => setFieldValue('production', detail.checked)}
                  checked={values.production}
                >
                  Mark as production run
                </Checkbox>
              </FormField>
            </Grid>
            {values.project?.steps?.length ? (
              <StepSelector
                selectedStepValue={values.selectedStepValue}
                projectDocId={values.project?.projectDocId}
                stepOptions={stepOptions}
                error={errors.selectedStep}
                onChange={({ detail }) => {
                  setFieldValue('selectedStepValue', detail.selectedOption.value);
                  updateChipNumber(
                    values.project?.projectId,
                    values.project?.pPartId,
                    detail.selectedOption?.pPartId,
                  );
                }}
                onValueChanged={handleStepChanged}
              />
            ) : null}
            {values.process === ProcessNames.FORMING ? (
              <Grid
                gridDefinition={values.material?.value === 'other'
                  ? [{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]
                  : [{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}
              >
                <MaterialSelector
                  setMaterial={(newMaterial) => setFieldValue('material', newMaterial)}
                  value={values.material}
                  error={errors.material}
                />
                {values.material?.value === 'other' ? (
                  <FormField label="Custom material" errorText={errors.otherMaterial}>
                    <Input
                      value={values.otherMaterial}
                      onChange={({ detail }) => setFieldValue('otherMaterial', detail.value)}
                    />
                  </FormField>
                ) : null}
                <PistonSelector
                  enabled={isOpen}
                  selectedValue={values.piston}
                  handleChange={onPistonChange}
                  errorText={errors.piston}
                  linkedParts={values.selectedStep?.linkedParts}
                  // The step value is an index within a line item, so the
                  // line item (project option value, unique per line item)
                  // is part of the key too.
                  stepKey={`${values.project?.value ?? ''}#${values.selectedStep?.value ?? ''}`}
                />
                <ToolSelector
                  projectDocId={values.project?.projectDocId}
                  error={errors.tool?.value}
                  selected={values.tool}
                  linkedParts={values.selectedStep?.linkedParts}
                  onChange={onToolChange}
                />
              </Grid>
            ) : null}
            {values.process === ProcessNames.BONDING ? (
              <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
                <FormField label="Cap Material" errorText={errors.capMaterial}>
                  <Select
                    selectedOption={values.capMaterial}
                    onChange={({ detail }) => {
                      setFieldValue('capMaterial', detail.selectedOption);
                    }}
                    options={CapMaterials.map((material) => ({
                      label: material,
                      value: material,
                    }))}
                    placeholder="Select cap material"
                  />
                </FormField>
                <FormField label="Number of chips" errorText={errors.numberOfChips}>
                  <Input
                    value={String(values.numberOfChips)}
                    onChange={({ detail }) => { setFieldValue('numberOfChips', detail.value); }}
                    inputMode="numeric"
                    type="number"
                  />
                </FormField>
                <FormField label="Press orientation" errorText={errors.pressOrientation}>
                  <Input value={values.pressOrientation} readOnly />
                </FormField>
              </Grid>
            ) : null}
            <Grid gridDefinition={[{ colspan: 10 }, { colspan: 2 }]}>
              <TravelerInput
                id="start-modal-travelers-input"
                travelers={values.travelers}
                errorText={(() => {
                  if (errors.travelers) {
                    return typeof errors.travelers === 'string' ? errors.travelers : 'Please double check all travelers';
                  }
                  return null;
                })()}
              />
              <FormField label="Skip" errorText={errors.skipTravelers}>
                <Checkbox
                  checked={values.skipTravelers}
                  onChange={({ detail }) => setFieldValue('skipTravelers', detail.checked)}
                >
                  Skip travelers
                </Checkbox>
              </FormField>
            </Grid>
            <FormField label="Hypothesis" errorText={errors.hypothesis} stretch>
              <Textarea
                onChange={({ detail }) => setFieldValue('hypothesis', detail.value)}
                value={values.hypothesis}
                placeholder="Any changes this run? What do you expect the outcome to be?"
              />
            </FormField>
            {configSection}
          </SpaceBetween>
        </Modal>
      </Form>
    </FormikProvider>
  );
}

StartRunModalShell.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  process: PropTypes.oneOf([ProcessNames.FORMING, ProcessNames.BONDING]).isRequired,
  press: PropTypes.string.isRequired,
  plcConfig: PropTypes.shape({}),
  // Called with { processLogId, startMs } once the process log exists.
  onStarted: PropTypes.func.isRequired,
  onContextChange: PropTypes.func,
  configSection: PropTypes.node,
  skipAction: PropTypes.func,
  configError: PropTypes.string,
  isStartDisabled: PropTypes.bool,
  startDisabledMessage: PropTypes.string,
  warningItems: PropTypes.arrayOf(PropTypes.shape({
    content: PropTypes.string.isRequired,
    dismissible: PropTypes.bool,
    header: PropTypes.string,
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  })),
  bondingDefaults: PropTypes.shape({
    pressOrientation: PropTypes.string,
  }),
};

export default StartRunModalShell;
